import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Globe2, Layers, FileText, Scale, MapPin, ShieldCheck, ArrowRight, Plus, Trash2,
  Package, Plane, ShoppingBag, Factory, Store, Gem, Shirt, Briefcase, Lock, Info,
} from "lucide-react";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { MarketingHero, SectionHead, GradientCta, fadeUp } from "../components/marketing/Blocks";

const FEATURES = [
  { icon: Globe2, title: "Global Reach", desc: "Ship products from India to customers around the world." },
  { icon: Layers, title: "Multiple International Services", desc: "Available courier and service options are presented in one workflow." },
  { icon: FileText, title: "Shipment Documentation", desc: "Organize required shipment information — items, HS codes and declared values." },
  { icon: Scale, title: "Weight Calculation", desc: "Volumetric and chargeable weight are calculated automatically per box." },
  { icon: MapPin, title: "International Tracking", desc: "Track international shipments from pickup to destination." },
  { icon: ShieldCheck, title: "Duties & Taxes Visibility", desc: "Clear messaging around duties and taxes — final charges depend on destination customs and carrier rules." },
];

const STEPS = [
  "Enter Destination", "Add Package Details", "Add Product Information", "Calculate Chargeable Weight",
  "Compare International Services", "Select Service", "Generate Shipment Documentation", "Schedule Pickup", "Track Globally",
];

const SELLERS = [
  { icon: ShoppingBag, t: "D2C Brands" }, { icon: Package, t: "Exporters" }, { icon: Store, t: "E-commerce Sellers" },
  { icon: Gem, t: "Handicraft Businesses" }, { icon: Shirt, t: "Fashion Brands" }, { icon: Factory, t: "Manufacturers" },
  { icon: Briefcase, t: "Small Export Businesses" },
];

// Real destination directory from the ShipAny international module (35 countries).
const REGIONS = {
  "North America": ["United States", "Canada", "Mexico"],
  "Europe": ["United Kingdom", "Germany", "France", "Netherlands", "Italy", "Spain", "Switzerland", "Turkey"],
  "Middle East": ["UAE", "Saudi Arabia", "Qatar", "Oman", "Kuwait", "Bahrain", "Israel"],
  "Asia": ["Singapore", "Japan", "China", "Hong Kong", "South Korea", "Malaysia", "Thailand", "Indonesia", "Philippines", "Vietnam", "Nepal", "Bangladesh", "Sri Lanka"],
  "Australia & New Zealand": ["Australia", "New Zealand"],
  "Africa": ["South Africa"],
  "South America": ["Brazil"],
};

const FORM_PREVIEW = [
  { title: "Sender Details", fields: ["Sender Address", "Pickup Warehouse", "City", "State", "Country", "Pincode"] },
  { title: "Destination Details", fields: ["Destination Country", "Destination Address", "Destination City", "State / Province", "Postal Code"] },
  { title: "Shipment & Items", fields: ["Number of Boxes", "Box Dimensions (L×W×H)", "Actual Weight per Box", "Item Name & HS Code", "Quantity & Item Value", "Country of Origin & Category"] },
];

function GlobeVisual() {
  const dests = [
    { l: "USA", x: 12, y: 26 }, { l: "UK", x: 42, y: 14 }, { l: "UAE", x: 60, y: 34 }, { l: "Europe", x: 50, y: 10 },
    { l: "Singapore", x: 78, y: 52 }, { l: "Australia", x: 86, y: 74 }, { l: "Canada", x: 8, y: 12 },
  ];
  return (
    <div className="relative mx-auto mt-12 aspect-square max-w-md" data-testid="intl-globe-visual">
      <div className="absolute inset-[8%] rounded-full border border-shipany-cyan/25 bg-[radial-gradient(circle_at_38%_32%,#0B2B57_0%,#071B3A_58%,#030B18_100%)] shadow-[0_0_80px_rgba(18,200,245,0.15)]">
        {[0.28, 0.5, 0.72].map((s) => (
          <span key={s} className="absolute rounded-full border border-shipany-cyan/15" style={{ inset: `${(1 - s) * 50}%` }} />
        ))}
        <span className="absolute left-1/2 top-[8%] bottom-[8%] w-px bg-shipany-cyan/15" />
        <span className="absolute left-[8%] right-[8%] top-1/2 h-px bg-shipany-cyan/15" />
      </div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        {dests.map((d, i) => (
          <path key={d.l} d={`M 55 55 Q ${(55 + d.x) / 2} ${Math.min(d.y, 55) - 18} ${d.x} ${d.y}`}
            fill="none" stroke={i % 2 ? "#FF9933" : "#12C8F5"} strokeWidth="0.5" vectorEffect="non-scaling-stroke"
            className="route-dash" style={{ animationDuration: `${5 + i}s` }} />
        ))}
      </svg>
      <div className="absolute left-[55%] top-[55%] -translate-x-1/2 -translate-y-1/2 text-center">
        <span className="pulse-dot mx-auto block h-3 w-3 rounded-full bg-[#FF9933]" />
        <span className="mt-1 block text-[10px] font-bold uppercase tracking-widest text-[#FF9933]">India</span>
      </div>
      {dests.map((d, i) => (
        <div key={d.l} className="absolute -translate-x-1/2 -translate-y-1/2 text-center" style={{ left: `${d.x}%`, top: `${d.y}%` }}>
          <span className="pulse-dot mx-auto block h-2 w-2 rounded-full bg-shipany-cyan" style={{ animationDelay: `${i * 0.35}s` }} />
          <span className="mt-0.5 block rounded bg-[#071B3A]/80 px-1 text-[8px] font-bold uppercase tracking-wider text-slate-300 backdrop-blur">{d.l}</span>
        </div>
      ))}
      <motion.div animate={{ rotate: 360 }} transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0">
        <span className="absolute left-1/2 top-[2%] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-xl border border-white/20 bg-white/10 backdrop-blur-xl">
          <Plane className="h-4 w-4 text-shipany-cyan" />
        </span>
      </motion.div>
    </div>
  );
}

function WeightCalculator() {
  const [boxes, setBoxes] = useState([{ l: "40", w: "30", h: "20", wt: "2" }]);
  const set = (i, k) => (e) => setBoxes(boxes.map((b, j) => (j === i ? { ...b, [k]: e.target.value.replace(/[^\d.]/g, "") } : b)));
  const rows = boxes.map((b) => {
    const l = parseFloat(b.l) || 0, w = parseFloat(b.w) || 0, h = parseFloat(b.h) || 0, wt = parseFloat(b.wt) || 0;
    const vol = (l * w * h) / 5000;
    const billed = Math.max(1, Math.ceil(Math.max(wt, vol)));
    return { vol, wt, billed };
  });
  const total = rows.reduce((s, r) => s + r.billed, 0);
  const inputCls = "mono w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none";
  return (
    <div className="rounded-3xl border border-white/15 bg-white/[0.05] p-6 backdrop-blur-xl sm:p-8" data-testid="intl-weight-calculator">
      <h3 className="font-display text-lg font-bold text-white">Chargeable Weight Calculator</h3>
      <p className="mt-1 text-xs text-slate-400">Volumetric = L×W×H ÷ 5000 · each box billed on the higher of actual vs volumetric, rounded up to the next 1 kg slab.</p>
      <div className="mt-5 space-y-3">
        {boxes.map((b, i) => (
          <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid={`intl-calc-box-${i + 1}`}>
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Box {i + 1}</p>
              {boxes.length > 1 && (
                <button onClick={() => setBoxes(boxes.filter((_, j) => j !== i))} data-testid={`intl-calc-remove-box-${i + 1}`}
                  className="rounded-lg p-1.5 text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[["l", "Length (cm)"], ["w", "Width (cm)"], ["h", "Height (cm)"], ["wt", "Actual Wt (kg)"]].map(([k, lbl]) => (
                <div key={k}><label className="mb-1 block text-[10px] font-semibold text-slate-500">{lbl}</label>
                  <input value={b[k]} onChange={set(i, k)} inputMode="decimal" data-testid={`intl-calc-box-${i + 1}-${k}`} className={inputCls} /></div>
              ))}
            </div>
            <p className="mono mt-3 text-[11px] text-slate-400" data-testid={`intl-calc-box-${i + 1}-billed`}>
              actual {rows[i].wt || 0} kg · volumetric {rows[i].vol.toFixed(2)} kg → billed <span className="font-bold text-shipany-cyan">{rows[i].billed} kg</span>
            </p>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => setBoxes([...boxes, { l: "", w: "", h: "", wt: "" }])} data-testid="intl-calc-add-box-btn"
          className="flex items-center gap-1.5 rounded-xl border border-shipany-cyan/40 px-4 py-2.5 text-xs font-bold text-shipany-cyan hover:bg-shipany-cyan/10">
          <Plus className="h-3.5 w-3.5" /> Add Box
        </button>
        <p className="rounded-xl border border-[#FF9933]/40 bg-[#FF9933]/10 px-5 py-2.5 font-display text-sm font-bold text-white" data-testid="intl-chargeable-weight">
          Chargeable Weight: <span className="text-[#FF9933]">{total} KG</span>
        </p>
      </div>
    </div>
  );
}

export default function InternationalShipping() {
  const [region, setRegion] = useState("North America");
  return (
    <MarketingLayout>
      <div data-testid="international-shipping-page">
        <MarketingHero kicker="ShipAny Global" title="International Shipping Made Simpler"
          sub="Help Indian businesses reach international customers with a streamlined international shipping experience for global shipments.">
          <motion.div {...fadeUp} className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link to="/signup" data-testid="intl-ship-now-btn"
              className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] px-8 py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.45)]">
              Ship Internationally <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/contact" data-testid="intl-talk-sales-btn"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/20 px-8 py-3.5 font-display text-sm font-bold text-white transition-colors hover:bg-white/5">
              Talk to Sales
            </Link>
          </motion.div>
          <GlobeVisual />
        </MarketingHero>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="intl-booking-preview-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="One booking flow" title="Everything the Shipment Needs, In One Place"
              sub="The ShipAny international booking flow captures sender, destination, boxes and item-level customs information — then calculates chargeable weight automatically." />
            <div className="mt-12 grid gap-5 lg:grid-cols-2">
              <div className="space-y-5">
                {FORM_PREVIEW.map((g, gi) => (
                  <motion.div key={g.title} {...fadeUp} transition={{ duration: 0.5, delay: gi * 0.08 }}
                    data-testid={`intl-form-group-${g.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                    <h3 className="font-display text-sm font-bold text-white">{g.title}</h3>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {g.fields.map((f) => (
                        <span key={f} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-slate-300">{f}</span>
                      ))}
                    </div>
                  </motion.div>
                ))}
                <motion.div {...fadeUp} className="overflow-hidden rounded-2xl border border-white/10" data-testid="intl-rates-preview">
                  <div className="mono grid grid-cols-6 gap-2 bg-white/5 px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    <span>Courier</span><span>Service</span><span>Transit</span><span>Chg. Wt</span><span>Est. Cost</span><span className="text-right">Action</span>
                  </div>
                  <div className="relative px-4 py-8 text-center">
                    <Lock className="mx-auto h-5 w-5 text-shipany-cyan" />
                    <p className="mt-2 text-sm font-semibold text-white">Live international rates during booking</p>
                    <p className="mx-auto mt-1 max-w-xs text-xs text-slate-400">Real courier services, transit times and costs are quoted from the connected carrier when you book — never sample numbers.</p>
                    <Link to="/signup" data-testid="intl-rates-signup-btn"
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#10B981] px-6 py-2.5 font-display text-xs font-bold text-[#030B18] transition-all hover:shadow-[0_8px_30px_rgba(16,185,129,0.4)]">
                      Sign Up to Compare <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </motion.div>
              </div>
              <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }}>
                <WeightCalculator />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="intl-features-section">
          <div className="mx-auto max-w-7xl px-5">
            <SectionHead kicker="Capabilities" title="International Shipping Features" />
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {FEATURES.map((f, i) => (
                <motion.div key={f.title} {...fadeUp} transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                  data-testid={`intl-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-shipany-cyan/40">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-shipany-blue/40 to-shipany-cyan/20">
                    <f.icon className="h-5 w-5 text-shipany-cyan" />
                  </span>
                  <h3 className="font-display mt-4 text-sm font-bold text-white">{f.title}</h3>
                  <p className="mt-1.5 text-xs leading-relaxed text-slate-400">{f.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="intl-how-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Workflow" title="How International Shipping Works" />
            <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {STEPS.map((s, i) => (
                <motion.div key={s} {...fadeUp} transition={{ duration: 0.4, delay: (i % 3) * 0.07 }}
                  data-testid={`intl-step-${i + 1}`}
                  className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3.5">
                  <span className="mono flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-shipany-blue/30 text-[11px] font-bold text-shipany-cyan">{i + 1}</span>
                  <span className="text-xs font-semibold text-slate-200">{s}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-shipany-navy py-20 sm:py-24" data-testid="intl-sellers-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Going global" title="Built for Indian Businesses Going Global" />
            <div className="mt-12 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {SELLERS.map((s, i) => (
                <motion.div key={s.t} {...fadeUp} transition={{ duration: 0.5, delay: (i % 4) * 0.07 }}
                  data-testid={`intl-seller-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 text-center transition-all duration-300 hover:-translate-y-1.5 hover:border-[#FF9933]/50">
                  <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FF9933]/15"><s.icon className="h-5 w-5 text-[#FF9933]" /></span>
                  <h3 className="font-display mt-3 text-xs font-bold text-white">{s.t}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-[#071B3A] py-20 sm:py-24" data-testid="intl-destinations-section">
          <div className="mx-auto max-w-6xl px-5">
            <SectionHead kicker="Destinations" title="Global Destinations"
              sub="Explore destination regions. Destination serviceability, services and rates are confirmed live during booking." />
            <div className="mt-10 flex flex-wrap justify-center gap-2">
              {Object.keys(REGIONS).map((r) => (
                <button key={r} onClick={() => setRegion(r)}
                  data-testid={`intl-region-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${region === r ? "border-shipany-cyan bg-shipany-cyan/15 text-shipany-cyan" : "border-white/15 text-slate-400 hover:border-white/30 hover:text-white"}`}>
                  {r}
                </button>
              ))}
            </div>
            <motion.div key={region} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
              className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="intl-country-list">
              {REGIONS[region].map((c) => (
                <span key={c} data-testid={`intl-country-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2 text-xs font-semibold text-slate-200">{c}</span>
              ))}
            </motion.div>
          </div>
        </section>

        <section className="bg-shipany-navy pb-20 pt-4" data-testid="intl-disclaimer-section">
          <div className="mx-auto max-w-4xl px-5">
            <div className="flex gap-3 rounded-2xl border border-[#FF9933]/30 bg-[#FF9933]/5 p-5">
              <Info className="h-5 w-5 shrink-0 text-[#FF9933]" />
              <p className="text-xs leading-relaxed text-slate-300" data-testid="intl-disclaimer">
                Rates, transit times, serviceability, duties, taxes and customs requirements may vary based on destination, shipment characteristics, carrier and applicable regulations.
              </p>
            </div>
          </div>
        </section>

        <GradientCta title="Take Your Business Global" sub="Ship from India to customers around the world with ShipAny Global."
          primary={["Ship Internationally", "/signup"]} secondary={["Talk to Sales", "/contact"]} testId="intl-final-cta" />
      </div>
    </MarketingLayout>
  );
}
